import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { SideNavigation, SideNavigationProps } from '@cloudscape-design/components';
import { useRole, UserRole } from '../contexts/RoleContext';

type NavigationItem = (SideNavigationProps.Link | SideNavigationProps.Section | SideNavigationProps.Divider) & {
  requiredRole?: UserRole | UserRole[];
};

export const NavigationMenu: React.FC = () => {
  const location = useLocation();
  const { hasPermission, isAdmin, isProduction, isLoading: roleLoading } = useRole();

  // Define all navigation items with optional role requirements
  const allNavigationItems: NavigationItem[] = useMemo(() => [
    {
      type: 'link',
      text: 'Getting Started',
      href: '/getting-started'
    },
    {
      type: 'link',
      text: 'Dashboard',
      href: '/dashboard'
    },
    {
      type: 'section',
      text: 'Settings',
      items: [
        {
          type: 'link',
          text: 'Devices',
          href: '/devices'
        },
        {
          type: 'link',
          text: 'Color Themes',
          href: '/themes'
        },
        {
          type: 'link',
          text: 'Location Lists',
          href: '/location-lists'
        }
      ]
    },
    {
      type: 'section',
      text: 'Production',
      requiredRole: ['Admin', 'Production'],
      items: [
        {
          type: 'link',
          text: 'Work Queue',
          href: '/production/work-queue',
          requiredRole: ['Admin', 'Production']
        }
      ]
    },
    {
      type: 'section',
      text: 'Configuration',
      requiredRole: 'Admin',
      items: [
        {
          type: 'link',
          text: 'Users',
          href: '/users',
          requiredRole: 'Admin'
        },
        {
          type: 'link',
          text: 'Data Products',
          href: '/data-products',
          requiredRole: 'Admin'
        },
        {
          type: 'link',
          text: 'System Config',
          href: '/configuration',
          requiredRole: 'Admin'
        },
        {
          type: 'link',
          text: 'Themes & Products',
          href: '/admin/themes',
          requiredRole: 'Admin'
        },
        {
          type: 'link',
          text: 'Base Maps',
          href: '/admin/base-maps',
          requiredRole: 'Admin'
        }
      ]
    },
    {
      type: 'section',
      text: 'Monitoring',
      items: [
        {
          type: 'link',
          text: 'Diagnostics',
          href: '/diagnostics',
          requiredRole: ['Admin', 'Production']
        },
        {
          type: 'link',
          text: 'Map Visualization',
          href: '/map'
        }
      ]
    },
    {
      type: 'section',
      text: 'Admin',
      requiredRole: 'Admin',
      items: [
        {
          type: 'link',
          text: 'Audit Log',
          href: '/admin/audit-log',
          requiredRole: 'Admin'
        }
      ]
    }
  ], []);

  // Filter navigation items based on user role
  const filteredNavigationItems = useMemo(() => {
    const filterItems = (items: NavigationItem[]): NavigationItem[] => {
      return items
        .filter(item => {
          // If item has no role requirement, include it
          if (!item.requiredRole) return true;
          
          // Check if user has required permission
          return hasPermission(item.requiredRole);
        })
        .map(item => {
          // If item is a section, filter its children
          if (item.type === 'section' && 'items' in item && item.items) {
            const filteredChildren = filterItems(item.items as NavigationItem[]);
            
            // Only include section if it has visible children
            if (filteredChildren.length === 0) return null;
            
            return {
              ...item,
              items: filteredChildren
            };
          }
          
          return item;
        })
        .filter((item): item is NavigationItem => item !== null);
    };

    return filterItems(allNavigationItems);
  }, [allNavigationItems, hasPermission, roleLoading]);

  return (
    <SideNavigation
      header={{
        href: '/',
        text: 'Live Maps'
      }}
      items={filteredNavigationItems}
      activeHref={location.pathname}
    />
  );
};
